import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { type AnalyticValues, useAnalytics } from "../../../../contexts/analytics/hooks";
import { useSnackbar } from "../../../../contexts/SnackbarContext";
import { downloadBlob } from "../../../../utils/downloadBlob";
import { createRecurringGapsWorkbook, type RecurringGapsExportLabels, type RecurringGapsExportView } from "../export/createRecurringGapsWorkbook";

const XLSX_MIME = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

type Params = {
	/** Read on click, so the file is the grid as it is at that moment. */
	getExport: () => { view: RecurringGapsExportView; labels: RecurringGapsExportLabels };
	fileName: string;
	analyticsProps: AnalyticValues;
};

/** Downloads the Recurring Gaps grid as Excel. Each run reports start, then success or error. */
export const useRecurringGapsExport = ({ getExport, fileName, analyticsProps }: Params) => {
	const { t } = useTranslation();
	const { track, trackError } = useAnalytics();
	const { showSnackbar } = useSnackbar();
	const [isDownloading, setIsDownloading] = useState(false);

	const download = useCallback(async (): Promise<void> => {
		setIsDownloading(true);
		track("hiring_gaps_export_start", analyticsProps);
		try {
			const { view, labels } = getExport();
			downloadBlob(await createRecurringGapsWorkbook(view, labels), fileName, XLSX_MIME);
			track("hiring_gaps_export_success", analyticsProps);
		} catch (error) {
			trackError("hiring_gaps_export_error", { ...analyticsProps, error_message: error instanceof Error ? error.message : String(error) });
			// `showSnackbar`, not `showErrorSnackbar`: that one sends its own error event, which would count
			// every failure twice next to the `_error` event above.
			showSnackbar(t("hiring.gaps.export.failed"), "error");
		} finally {
			setIsDownloading(false);
		}
	}, [getExport, fileName, analyticsProps, track, trackError, showSnackbar, t]);

	return { download, isDownloading };
};
